import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  exportAccountMapping,
  exportAdvisoryMapping,
  exportMultiEntityOwnership,
  exportRelatedContact,
  exportRelatedLoanAccount,
  fetchAdvisoryMappingDetails,
  fetchAdvisoryMappingList,
  updateAdvisoryMappingGroup,
  uploadAdvisoryMappingData,
  upsertAccountMapping,
  upsertMultiEntityOwnership,
  upsertRelatedContacts,
  upsertRelatedLoanAccounts,
  upsertUserAccess,
} from '../api/advisoryMapping'
import { queryKeys } from '../api/queryKeys'
import type {
  AccountMappingRow,
  AdvisoryMappingUploadInput,
  MultiEntityOwnershipRow,
  RelatedContactRow,
  RelatedLoanAccountRow,
  UserAccessRow,
} from '../types/advisoryMapping'

export function useAdvisoryMappingList(clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisoryMapping.list(clientId),
    queryFn: () => fetchAdvisoryMappingList(clientId),
    enabled: !!clientId,
  })
}

export function useAdvisoryMappingDetails(platformId: string, clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisoryMapping.detail(platformId, clientId),
    queryFn: () => fetchAdvisoryMappingDetails(platformId, clientId),
    enabled: !!platformId && !!clientId,
  })
}

export function useUpdateAdvisoryMappingGroupMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ platformId, groupName }: { platformId: string; groupName: string }) =>
      updateAdvisoryMappingGroup(platformId, groupName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.list(clientId) })
    },
  })
}

export function useUpsertAccountMappingMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rows: AccountMappingRow[]) => upsertAccountMapping(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
    },
  })
}

export function useUpsertMultiEntityOwnershipMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rows: MultiEntityOwnershipRow[]) => upsertMultiEntityOwnership(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
    },
  })
}

export function useUpsertRelatedContactsMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rows: RelatedContactRow[]) => upsertRelatedContacts(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
    },
  })
}

export function useUpsertRelatedLoanAccountsMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rows: RelatedLoanAccountRow[]) => upsertRelatedLoanAccounts(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
    },
  })
}

export function useUpsertUserAccessMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rows: UserAccessRow[]) => upsertUserAccess(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
    },
  })
}

export function useUploadAdvisoryMappingMutation(platformId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AdvisoryMappingUploadInput) => uploadAdvisoryMappingData(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.detail(platformId, clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisoryMapping.list(clientId) })
    },
  })
}

export function useExportAdvisoryMappingMutation() {
  return useMutation({ mutationFn: (platformId: string) => exportAdvisoryMapping(platformId) })
}

export function useExportAccountMappingMutation() {
  return useMutation({ mutationFn: (platformId: string) => exportAccountMapping(platformId) })
}

export function useExportMultiEntityOwnershipMutation() {
  return useMutation({ mutationFn: (platformId: string) => exportMultiEntityOwnership(platformId) })
}

export function useExportRelatedContactMutation() {
  return useMutation({ mutationFn: (platformId: string) => exportRelatedContact(platformId) })
}

export function useExportRelatedLoanAccountMutation() {
  return useMutation({ mutationFn: (platformId: string) => exportRelatedLoanAccount(platformId) })
}
